/**
 * reCAPTCHA Manager
 * Proper implementation of reCAPTCHA with error handling, retry logic, and state management
 */


export interface RecaptchaConfig {
  siteKey: string;
  enabled: boolean;
  timeout?: number;
  maxRetries?: number;
  retryDelay?: number;
  action?: string; // Action parameter for reCAPTCHA Enterprise (e.g., 'LOGIN', 'SIGNUP', 'REQUEST')
}

export interface RecaptchaResult {
  success: boolean;
  token?: string;
  error?: string;
  retryCount?: number;
}

class RecaptchaManager {
  private static instance: RecaptchaManager;
  private config: RecaptchaConfig;
  private isExecuting = false;
  private retryCount = 0;
  private recaptchaRef: any = null;
  private currentExecutionPromise: Promise<string> | null = null;
  private pendingExecutionPromise: Promise<RecaptchaResult> | null = null; // Track current execution to queue requests

  private constructor(config: RecaptchaConfig) {
    this.config = {
      timeout: 30000, // 30 seconds
      maxRetries: 3,
      retryDelay: 1000, // 1 second
      ...config
    };
  }

  public static getInstance(config?: RecaptchaConfig): RecaptchaManager {
    if (!RecaptchaManager.instance) {
      if (!config) {
        throw new Error('RecaptchaManager requires configuration on first initialization');
      }
      RecaptchaManager.instance = new RecaptchaManager(config);
    }
    return RecaptchaManager.instance;
  }

  public static resetInstance(): void {
    if (RecaptchaManager.instance) {
      RecaptchaManager.instance.reset();
      RecaptchaManager.instance = undefined as any;
    }
  }

  public setRecaptchaRef(ref: any): void {
    console.log('[RecaptchaManager] setRecaptchaRef called:', { 
      refExists: !!ref,
      refType: ref ? typeof ref : 'null',
      hasExecuteAsync: ref && typeof ref.executeAsync === 'function',
      previousRefExists: !!this.recaptchaRef
    });
    this.recaptchaRef = ref;
  }

  private checkIfDisabled(): RecaptchaResult | null {
    if (!this.config.enabled) {
      console.log('[RecaptchaManager] Execute skipped - disabled');
      return { success: true, token: 'disabled' };
    }
    return null;
  }


  private validateRef(): RecaptchaResult | null {
    // For Enterprise API, we don't need executeAsync on the ref
    // The ref is only needed for widget rendering, not execution
    // Execution uses grecaptcha.enterprise.execute() directly
    if (!this.recaptchaRef) {
      console.error('[RecaptchaManager] Execute failed - widget ref not set');
      return { 
        success: false, 
        error: 'reCAPTCHA not initialized - widget ref not set' 
      };
    }
    return null;
  }

  private validateGrecaptchaEnterprise(): RecaptchaResult | null {
    if (typeof window === 'undefined' || !(window as any).grecaptcha || !(window as any).grecaptcha.enterprise) {
      console.error('[RecaptchaManager] Execute failed - grecaptcha.enterprise not available');
      return { 
        success: false, 
        error: 'reCAPTCHA Enterprise not initialized - script not loaded' 
      };
    }
    return null;
  }

  private createExecutionPromise(): Promise<RecaptchaResult> {
    this.isExecuting = true;
    this.retryCount = 0;

    const executionPromise = (async (): Promise<RecaptchaResult> => {
      try {
        console.log('[RecaptchaManager] Starting execution with retry');
        return await this.executeWithRetry();
      } finally {
        // Always clear state in finally block to ensure cleanup even on errors
        this.isExecuting = false;
        this.pendingExecutionPromise = null;
        console.log('[RecaptchaManager] Execution completed, state cleared');
      }
    })();

    // Store the promise IMMEDIATELY so any concurrent calls will wait
    this.pendingExecutionPromise = executionPromise;
    return executionPromise;
  }

  private formatSiteKey(): string {
    if (this.config.siteKey) return `${this.config.siteKey.substring(0, 10)}...`;
    return 'empty';
  }

  private getGrecaptchaState(): { hasGrecaptcha: boolean; hasEnterprise: boolean } {
    const hasWindow = typeof window !== 'undefined';
    if (!hasWindow) return { hasGrecaptcha: false, hasEnterprise: false };
    const grecaptcha = (window as any).grecaptcha;
    return {
      hasGrecaptcha: !!grecaptcha,
      hasEnterprise: !!(grecaptcha && grecaptcha.enterprise),
    };
  }

  private logExecuteState(): void {
    const grecaptchaState = this.getGrecaptchaState();
    console.log('[RecaptchaManager] execute() called:', {
      enabled: this.config.enabled,
      siteKey: this.formatSiteKey(),
      action: this.config.action || 'LOGIN',
      isExecuting: this.isExecuting,
      hasPendingExecution: !!this.pendingExecutionPromise,
      hasRef: !!this.recaptchaRef,
      ...grecaptchaState,
    });
  }

  private checkPendingExecution(): RecaptchaResult | null {
    if (!this.pendingExecutionPromise) return null;
    console.log('[RecaptchaManager] Execution already in progress, waiting for it to complete...');
    return null;
  }

  private getPendingOrNull(): Promise<RecaptchaResult> | null {
    return this.pendingExecutionPromise;
  }

  private validateAll(): RecaptchaResult | null {
    return this.validateRef() ?? this.validateGrecaptchaEnterprise();
  }

  public async execute(): Promise<RecaptchaResult> {
    this.logExecuteState();

    const disabledResult = this.checkIfDisabled();
    if (disabledResult) return disabledResult;

    this.checkPendingExecution();
    const pending = this.getPendingOrNull();
    if (pending) return pending;

    const validationError = this.validateAll();
    if (validationError) return validationError;

    return this.createExecutionPromise();
  }


  private async resetWidgetBeforeRetry(): Promise<void> {
    const canReset = !!this.recaptchaRef && typeof this.recaptchaRef.reset === 'function';
    if (!canReset) return;

    try {
      console.log('[RecaptchaManager] Resetting widget after max retries');
        this.recaptchaRef.reset();
      } catch (resetError) {
      console.warn('[RecaptchaManager] Widget reset failed:', resetError);
    }
  }

  private createSuccessResult(token: string, attemptCount: number): RecaptchaResult {
    this.retryCount = attemptCount - 1;
    return {
      success: true,
      token,
      retryCount: this.retryCount
    };
  }

  private createFailureResult(error: unknown, maxAttempts: number, attemptCount: number): RecaptchaResult {
    this.retryCount = attemptCount;
    const errorMessage = error instanceof Error ? error.message : String(error);
    return {
      success: false,
      error: `reCAPTCHA failed after ${maxAttempts} attempts: ${errorMessage}`,
      retryCount: this.retryCount
    };
  }

  private validateToken(token: any): { isValid: boolean; error?: string } {
    const INVALID_TOKEN_ERROR = 'reCAPTCHA returned invalid or empty token';
    const isValidString = typeof token === 'string' && token.trim().length > 0;
    return isValidString ? { isValid: true } : { isValid: false, error: INVALID_TOKEN_ERROR };
  }

  private clearExecutionPromise(executePromise: Promise<any>): void {
    if (this.currentExecutionPromise === executePromise) {
      this.currentExecutionPromise = null;
    }
  }

  private processValidToken(token: string, resolve: (value: string) => void): void {
    console.log('[RecaptchaManager] executeAsync resolved:', { 
      hasToken: !!token,
      tokenType: typeof token,
      token: token ? `${token.substring(0, 20)}...` : String(token)
    });
    resolve(token);
  }

  private processInvalidToken(token: any, reject: (reason?: any) => void): void {
    console.error('[RecaptchaManager] executeAsync returned invalid token:', { 
      token, 
      type: typeof token,
      isString: typeof token === 'string',
      isEmpty: typeof token === 'string' && token.trim().length === 0
    });
    reject(new Error('reCAPTCHA returned invalid or empty token'));
  }

  private handleTokenResolution(
    token: any,
    executePromise: Promise<any>,
    isResolvedRef: { current: boolean },
    resolve: (value: string) => void,
    reject: (reason?: any) => void
  ): void {
    this.clearExecutionPromise(executePromise);
    
    if (isResolvedRef.current) {
      return;
    }
    
    isResolvedRef.current = true;
    
    const validation = this.validateToken(token);
    if (validation.isValid) {
      this.processValidToken(token, resolve);
    } else {
      this.processInvalidToken(token, reject);
    }
  }

  private handleExecutionSuccess(token: string, attemptCount: number): RecaptchaResult {
    console.log('[RecaptchaManager] executeOnce succeeded:', { 
      hasToken: !!token,
      token: token ? `${token.substring(0, 20)}...` : 'empty'
    });
    this.currentExecutionPromise = null;
    return this.createSuccessResult(token, attemptCount);
  }

  private async handleMaxRetriesReached(error: unknown, maxAttempts: number, attemptCount: number): Promise<RecaptchaResult> {
    console.error('[RecaptchaManager] Max retries reached, giving up');
    // Reset widget when max retries are reached to clear any loading state
    // This is the only place we reset - not between retries to avoid race conditions
    await this.resetWidgetBeforeRetry();
    return this.createFailureResult(error, maxAttempts, attemptCount);
  }

  private prepareForRetry(): RecaptchaResult | null {
    this.currentExecutionPromise = null;
    
    if (!this.recaptchaRef || typeof (this.recaptchaRef as any).executeAsync !== 'function') {
      return {
        success: false,
        error: 'reCAPTCHA widget became invalid between retries',
        retryCount: this.retryCount
      };
    }
    
    console.log('[RecaptchaManager] Widget verified ready, retrying immediately');
    return null;
  }

  private tryResetWidget(): void {
    if (!this.recaptchaRef || typeof this.recaptchaRef.reset !== 'function') return;
    try {
      this.recaptchaRef.reset();
    } catch (resetError) {
      console.warn('[RecaptchaManager] Widget reset failed in fallback:', resetError);
    }
  }

  private handleFallbackFailure(lastError: unknown, attemptCount: number): RecaptchaResult {
    this.retryCount = attemptCount;
    this.currentExecutionPromise = null;
    this.tryResetWidget();
    const errorMessage = lastError
      ? `reCAPTCHA execution failed: ${lastError instanceof Error ? lastError.message : String(lastError)}`
      : 'reCAPTCHA execution failed';
    return { success: false, error: errorMessage, retryCount: this.retryCount };
  }

  private static readonly RACE_CONDITION_PATTERNS = [
    'widget state was cleared',
    'Cannot set properties of undefined',
  ];

  private isRaceConditionError(error: unknown): boolean {
    const errorMessage = error instanceof Error ? error.message : String(error);
    return RecaptchaManager.RACE_CONDITION_PATTERNS.some(p => errorMessage.includes(p));
  }

  private handleRetryError(error: unknown): RecaptchaResult | null {
    this.currentExecutionPromise = null;
    if (!this.isRaceConditionError(error)) return null;
    if (!this.recaptchaRef || typeof (this.recaptchaRef as any).executeAsync !== 'function') {
      return {
        success: false,
        error: 'reCAPTCHA widget became invalid after race condition',
        retryCount: this.retryCount
      };
    }
    return null;
  }

  private async handleRetryAttemptError(
    error: unknown,
    attemptCount: number,
    maxAttempts: number
  ): Promise<RecaptchaResult | null> {
    console.error('[RecaptchaManager] executeOnce failed:', error, 'Attempt:', attemptCount);

    const retryErrorResult = this.handleRetryError(error);
    if (retryErrorResult) return retryErrorResult;

    if (attemptCount >= maxAttempts) {
      return await this.handleMaxRetriesReached(error, maxAttempts, attemptCount);
    }

    return this.prepareForRetry();
  }

  private async runRetryLoop(maxAttempts: number): Promise<{ result?: RecaptchaResult; lastError: unknown; attemptCount: number }> {
    let attemptCount = 0;
    let lastError: unknown = null;

    while (attemptCount < maxAttempts) {
      attemptCount++;

      try {
        console.log('[RecaptchaManager] Attempting executeOnce, attempt:', attemptCount);
        const token = await this.executeOnce();
        return { result: this.handleExecutionSuccess(token, attemptCount), lastError, attemptCount };
      } catch (error) {
        lastError = error;
        const errorResult = await this.handleRetryAttemptError(error, attemptCount, maxAttempts);
        if (errorResult) return { result: errorResult, lastError, attemptCount };
      }
    }

    return { lastError, attemptCount };
  }

  private async executeWithRetry(): Promise<RecaptchaResult> {
    const maxAttempts = this.config.maxRetries ?? 3;

    console.log('[RecaptchaManager] executeWithRetry started:', { 
      maxRetries: maxAttempts,
      retryCount: this.retryCount
    });

    const { result, lastError, attemptCount } = await this.runRetryLoop(maxAttempts);
    return result ?? this.handleFallbackFailure(lastError, attemptCount);
  }

  private normalizeError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }

  private cancelPendingExecution(): void {
    if (!this.currentExecutionPromise) return;
    this.currentExecutionPromise.catch(() => {});
    this.currentExecutionPromise = null;
  }

  private getEnterpriseExecuteFn(): ((siteKey: string, opts: any) => Promise<any>) | null {
    const currentEnterprise = (window as any).grecaptcha?.enterprise;
    const isValid = currentEnterprise && typeof currentEnterprise.execute === 'function';
    return isValid ? currentEnterprise.execute.bind(currentEnterprise) : null;
  }

  private validateExecutePromise(promise: any): boolean {
    return !!promise && typeof promise.then === 'function';
  }

  private executeEnterpriseRecaptcha(
    action: string,
    isResolvedRef: { current: boolean },
    resolve: (value: string) => void,
    reject: (reason?: any) => void,
    rejectOnce: (error: unknown) => void
  ): void {
    const executeFn = this.getEnterpriseExecuteFn();
    if (!executeFn) {
      rejectOnce(new Error('grecaptcha.enterprise.execute became unavailable'));
      return;
    }

    const promise = executeFn(this.config.siteKey, { action });
    if (!this.validateExecutePromise(promise)) {
      rejectOnce(new Error('grecaptcha.enterprise.execute did not return a promise'));
      return;
    }

    this.currentExecutionPromise = promise;

    promise
      .then((token: any) => this.handleTokenResolution(token, promise, isResolvedRef, resolve, reject))
      .catch((error: any) => rejectOnce(error));
  }

  private async executeOnce(): Promise<string> {
    this.cancelPendingExecution();

    await this.waitForEnterpriseReady();

    const action = this.config.action || 'LOGIN';
    const executionTimeout = this.config.timeout ?? 30000;

    const executionPromise = new Promise<string>((resolve, reject) => {
      const isResolvedRef = { current: false };

      const rejectOnce = (error: unknown) => {
        if (isResolvedRef.current) return;
        isResolvedRef.current = true;
        this.currentExecutionPromise = null;
        reject(this.normalizeError(error));
      };

      try {
        const grecaptchaEnterprise = (window as any).grecaptcha.enterprise;
        grecaptchaEnterprise.ready(() => {
          try {
            this.executeEnterpriseRecaptcha(action, isResolvedRef, resolve, reject, rejectOnce);
          } catch (error) {
            rejectOnce(error);
          }
        });
      } catch (error) {
        rejectOnce(error);
      }
    });

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`reCAPTCHA execution timed out after ${executionTimeout}ms`));
      }, executionTimeout);
    });

    return Promise.race([executionPromise, timeoutPromise]);
  }

  private waitForEnterpriseReady(): Promise<void> {
    const enterprise = (window as any).grecaptcha?.enterprise;
    if (enterprise && typeof enterprise.execute === 'function') {
      return Promise.resolve();
    }

    const timeout = this.config.timeout ?? 30000;
    const pollInterval = 200;

    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const check = () => {
        const ent = (window as any).grecaptcha?.enterprise;
        if (ent && typeof ent.execute === 'function') {
          resolve();
          return;
        }
        if (Date.now() - startTime > timeout) {
          reject(new Error('grecaptcha.enterprise.execute not available within timeout'));
          return;
        }
        setTimeout(check, pollInterval);
      };

      check();
    });
  }


  public reset(): void {
    // Clear the widget's internal execution promise
    this.currentExecutionPromise = null;
    
    // Reset the widget
    if (this.recaptchaRef) {
      try {
        this.recaptchaRef.reset();
      } catch (error) {
        console.warn('reCAPTCHA reset failed:', error);
      }
    }
    
    // Reset state
    this.retryCount = 0;
    this.isExecuting = false;
    
    // Note: We do NOT clear pendingExecutionPromise here because:
    // 1. If there's an active execution, we want it to complete naturally
    // 2. Clearing it would cause waiting requests to start new executions
    // 3. The execution will clear it itself when it completes
    // If you need to cancel an active execution, you'd need a different mechanism
  }

  public isReady(): boolean {
    const hasRef = !!this.recaptchaRef;
    const hasEnterprise = typeof window !== 'undefined' &&
      !!(window as any).grecaptcha?.enterprise;
    
    return hasRef && hasEnterprise;
  }

  public getSiteKey(): string {
    return this.config.siteKey;
  }

  public getIsExecuting(): boolean {
    // Check if execution is in progress
    // Either isExecuting flag is true OR there's a pending execution promise
    return this.isExecuting || this.pendingExecutionPromise !== null;
  }

  public updateConfig(newConfig: Partial<RecaptchaConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }
}

export default RecaptchaManager;
